// ═══════════════════════════════════════════════════════════════════════════
// Revisão e emissão do Relatório PMF
// 1) gera as análises da IA seção a seção (JSON estruturado, via Worker)
// 2) valida que todo número citado existe nos dados; refaz uma vez se não existir
// 3) consultor revisa/edita; o PDF só é liberado com todas as seções aprovadas
// ═══════════════════════════════════════════════════════════════════════════
import { useState, useMemo, useRef } from "react";
import { blocoDados, SECOES, validarNumeros, limparTexto, calcularRating, rotuloCurto, textoCompetencia, comparacaoPonte, ponteEbitda, ponteLucro, linhasPonte, rotuloStatus } from "../lib/financeiro";
import { gerarHTMLRelatorio } from "./template";

export const WORKER_URL = "https://pmf-dashboard-api.cleparnoff.workers.dev";
const CHAVES_GERAIS = ["roic", "roe", "margemEbitda", "margemLiquida", "lc", "ls", "kanitz", "endiv", "divLiqEbitda", "cobJuros", "ciclo", "pmr", "crescimentoReceita", "crescimentoReal", "regraDos40"];
const NOMES = { resumo: "Sumário executivo", rentabilidade: "Rentabilidade e retorno", liquidez: "Liquidez e solvência", giro: "Capital de giro e ciclos", endividamento: "Estrutura de capital e endividamento", resultado: "Resultado do período", orcado: "Realizado × orçado", parecer: "Parecer final" };
const ST = { pendente: ["Pendente", "#6B7280"], gerando: ["Gerando…", "#1E40AF"], ok: ["Aprovada", "#15803D"], revisar: ["Revisar números", "#B45309"], erro: ["Erro", "#B91C1C"] };

const S = {
  fundo: { position: "fixed", inset: 0, background: "rgba(15,30,61,.55)", zIndex: 300, overflowY: "auto", padding: "24px 16px", fontFamily: "Inter,'Source Sans 3',system-ui,sans-serif" },
  caixa: { maxWidth: 920, margin: "0 auto", background: "#fff", borderTop: "4px solid #0F1E3D", boxShadow: "0 10px 40px rgba(0,0,0,.25)" },
  btn: (cor, cheio) => ({ padding: "9px 16px", border: `1px solid ${cor}`, background: cheio ? cor : "transparent", color: cheio ? "#fff" : cor, fontWeight: 600, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", letterSpacing: ".02em" }),
  ta: { width: "100%", minHeight: 70, border: "1px solid #D9DCE3", padding: 8, fontFamily: "inherit", fontSize: 13, lineHeight: 1.45, boxSizing: "border-box" },
};

// Todos os textos de uma análise (para validação de números)
function textos(a) {
  if (!a) return "";
  return [a.parecer, a.conclusao, a.por_que_importa, ...(a.o_que_mostram || []), ...(a.pontos_fortes || []), ...(a.pontos_atencao || []), ...(a.prioridades || []), ...(a.recomendacoes || []).flatMap(r => [r.acao, r.impacto, r.responsavel])].filter(Boolean).join(" \n ");
}
function limpar(a) {
  const L = v => typeof v === "string" ? limparTexto(v) : v;
  const out = {};
  Object.entries(a || {}).forEach(([k, v]) => {
    if (Array.isArray(v)) out[k] = v.map(x => typeof x === "object" && x ? Object.fromEntries(Object.entries(x).map(([kk, vv]) => [kk, L(vv)])) : L(x)).filter(x => x !== "");
    else out[k] = L(v);
  });
  return out;
}

export default function RelatorioPMF({ serie, idx, comp, client, orcadoSerie, demo, cenario, reajuste, getToken, onFechar, onEmitido }) {
  const j = comp?.idx ?? null;
  const rating = useMemo(() => calcularRating(serie, idx), [serie, idx]);
  const temOrcado = !!orcadoSerie && orcadoSerie.periodos.includes(serie.periodos[idx]);
  const secoes = useMemo(() => ["resumo", "rentabilidade", "liquidez", "giro", "endividamento", "resultado", ...(temOrcado ? ["orcado"] : []), "parecer"], [temOrcado]);
  const [est, setEst] = useState(() => Object.fromEntries(secoes.map(s => [s, { status: "pendente" }])));
  const [editando, setEditando] = useState(null);
  const [rodando, setRodando] = useState(false);
  const [aviso, setAviso] = useState("");
  const cancel = useRef(false);

  const competencia = textoCompetencia(serie.periodos[idx], serie.ind.mesesDRE?.[idx]);
  const comparacao = j != null ? `${rotuloCurto(serie.periodos[j])} (${comp.tipo === "ano" ? "mesmo período do ano anterior" : "período anterior"})` : "sem período de comparação";

  function bloco(secao) {
    if (secao === "resumo") return blocoDados(serie, idx, j, CHAVES_GERAIS);
    if (secao === "parecer") {
      // O parecer enxerga também o cenário proposto (se houver), para poder citá-lo
      const cen = cenario?.resumo?.linhas ? cenario.resumo.linhas.map(l => ({ chave: l.chave, indicador: `Cenário proposto — ${l.indicador}`, valor: l.cenario, anterior: l.atual, variacao: l.delta, faixa: "—", status: l.statusCenario, statusTxt: rotuloStatus(l.chave, l.statusCenario) })) : [];
      const rat = cenario?.resumo ? [{ chave: "rating", indicador: "Cenário proposto — rating PMF", valor: cenario.resumo.ratingCenario?.nota || "—", anterior: cenario.resumo.ratingAtual?.nota || "—", variacao: "—", faixa: "—", status: "informativo", statusTxt: "Informativo" }] : [];
      return [...blocoDados(serie, idx, j, CHAVES_GERAIS), ...rat, ...cen];
    }
    if (secao === "resultado") {
      // Pontes do EBITDA e do lucro (só quando há período comparável com a mesma janela de meses)
      const cp = comparacaoPonte(serie, idx);
      const pontes = cp ? [...linhasPonte(ponteEbitda(serie, idx, cp.idx, { reajuste })), ...linhasPonte(ponteLucro(serie, idx, cp.idx))] : [];
      return [...blocoDados(serie, idx, j, SECOES.resultado), ...pontes];
    }
    if (secao === "orcado") {
      const jo = orcadoSerie.periodos.indexOf(serie.periodos[idx]);
      return ["receitaLiquida", "ebitda", "lucroLiquido", "margemEbitda", "roic", "lc", "endiv", "pmr"].map(k => {
        const l = blocoDados(serie, idx, null, [k])[0]; if (!l) return null;
        const o = orcadoSerie.ind[k]?.[jo]; if (o == null) return null;
        return { ...l, anterior: blocoDados(orcadoSerie, jo, null, [k])[0]?.valor || "—", variacao: "realizado vs. orçado" };
      }).filter(Boolean);
    }
    return blocoDados(serie, idx, j, SECOES[secao]);
  }
  const permitidos = linhas => linhas.map(l => [l.valor, l.anterior, l.variacao, l.faixa].join(" ")).join(" ") + ` ${rating?.nota || ""}`;

  async function chamar(secao, linhas, feedback) {
    const token = await getToken();
    const r = await fetch(WORKER_URL, {
      method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ type: "analise", secao, empresa: client.name, setor: client.sector, competencia, linhas, feedback,
        contexto: { comparacao, rating: rating?.nota ? `${rating.nota} — ${rating.descricao}${rating.teto ? ` (limitado por ${rating.teto})` : ""}` : null, notas: serie.notas[idx] || [] } }),
    });
    const js = await r.json().catch(() => ({}));
    if (!r.ok || !js.ok) throw new Error(js.erro || `Falha ${r.status}`);
    return limpar(js.analise);
  }

  async function gerar(secao) {
    setEst(p => ({ ...p, [secao]: { status: "gerando" } }));
    try {
      const linhas = bloco(secao);
      if (!linhas.length) throw new Error("Sem dados para esta seção no período.");
      let a = await chamar(secao, linhas);
      let v = validarNumeros(textos(a), permitidos(linhas));
      if (!v.ok) { a = await chamar(secao, linhas, v.invalidos.join(", ")); v = validarNumeros(textos(a), permitidos(linhas)); }
      setEst(p => ({ ...p, [secao]: { status: v.ok ? "ok" : "revisar", dados: a, invalidos: v.invalidos, linhas } }));
    } catch (e) {
      setEst(p => ({ ...p, [secao]: { status: "erro", erro: e.message } }));
    }
  }

  async function gerarTodas() {
    setRodando(true); cancel.current = false; setAviso("");
    const fila = secoes.filter(s => est[s]?.status !== "ok");
    const trabalhador = async () => { while (fila.length && !cancel.current) await gerar(fila.shift()); };
    await Promise.all([trabalhador(), trabalhador()]); // 2 em paralelo
    setRodando(false);
  }

  function salvarEdicao(secao, novo) {
    const a = limpar(novo); const linhas = est[secao].linhas || bloco(secao);
    const v = validarNumeros(textos(a), permitidos(linhas));
    setEst(p => ({ ...p, [secao]: { ...p[secao], dados: a, status: v.ok ? "ok" : "revisar", invalidos: v.invalidos, editado: true } }));
    setEditando(null);
  }

  const prontas = secoes.filter(s => est[s]?.status === "ok").length;
  const liberado = prontas === secoes.length;

  function abrirPDF() {
    if (!liberado) return;
    const analises = Object.fromEntries(secoes.map(s => [s, est[s].dados]));
    const html = gerarHTMLRelatorio({ serie, i: idx, j, tipoComp: comp?.tipo, empresa: client.name, setor: client.sector, analises, rating, orcadoSerie, demo, cenario, reajuste, emissao: new Date().toLocaleDateString("pt-BR") });
    const w = window.open("", "_blank");
    if (!w) { setAviso("O navegador bloqueou a nova janela. Permita pop-ups para painel.pmfcontroladoria.com.br e clique novamente."); return; }
    w.document.open(); w.document.write(html); w.document.close();
    onEmitido?.({ periodo: serie.periodos[idx], rating: rating?.nota || null, analises, cenario: cenario ? { nome: cenario.nome, descricao: cenario.descricao || "", alavancas: cenario.alavancas } : null, emitidoEm: new Date().toISOString() });
  }

  return (
    <div style={S.fundo}>
      <div style={S.caixa}>
        <div style={{ padding: "22px 28px 16px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#C9A84C", fontWeight: 600 }}>Relatório de análise das demonstrações</div>
            <div style={{ fontFamily: "'Playfair Display',Georgia,serif", fontSize: 24, fontWeight: 600, color: "#0F1E3D" }}>{client.name}</div>
            <div style={{ fontSize: 13, color: "#6B7280" }}>{competencia} · comparação: {comparacao} · rating PMF: <b style={{ color: "#0F1E3D" }}>{rating?.nota || "—"}</b>{demo && <b style={{ color: "#B91C1C" }}> · DEMONSTRAÇÃO</b>}</div>
          </div>
          <button onClick={() => { cancel.current = true; onFechar(); }} style={S.btn("#6B7280")}>Fechar</button>
        </div>

        <div style={{ padding: "16px 28px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", background: "#F8F7F2" }}>
          <button disabled={rodando || liberado} onClick={gerarTodas} style={{ ...S.btn("#0F1E3D", true), opacity: rodando || liberado ? .5 : 1 }}>{prontas ? "Gerar seções pendentes" : "Gerar análises com IA"}</button>
          <button disabled={!liberado} onClick={abrirPDF} title={liberado ? "" : "Todas as seções precisam estar aprovadas"} style={{ ...S.btn("#15803D", true), opacity: liberado ? 1 : .4, cursor: liberado ? "pointer" : "not-allowed" }}>Abrir relatório para PDF</button>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 12.5, color: "#1F2937", fontWeight: 600 }}>{prontas} de {secoes.length} análises aprovadas</div>
            <div style={{ height: 4, background: "#E5E7EB", marginTop: 4 }}><div style={{ height: 4, width: `${prontas / secoes.length * 100}%`, background: "#15803D", transition: "width .3s" }} /></div>
          </div>
        </div>
        {aviso && <div style={{ margin: "0 28px", padding: "8px 12px", border: "1px solid #B45309", color: "#B45309", fontSize: 13 }}>{aviso}</div>}
        {!liberado && prontas > 0 && !rodando && <div style={{ padding: "6px 28px 0", fontSize: 12, color: "#6B7280" }}>O PDF fica bloqueado enquanto houver seção pendente, com erro ou com número que não consta nos dados.</div>}

        <div style={{ padding: "8px 28px 28px" }}>
          {secoes.map(s => {
            const e = est[s] || {}; const [rot, cor] = ST[e.status || "pendente"];
            return (
              <div key={s} style={{ borderBottom: "1px solid #E5E7EB", padding: "14px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 600, color: "#0F1E3D", fontSize: 14, flex: 1 }}>{NOMES[s]}{e.editado && <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 400 }}> · editado pelo consultor</span>}</div>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: cor }}>● {rot}</span>
                  {e.dados && <button onClick={() => setEditando(editando === s ? null : s)} style={S.btn("#0F1E3D")}>{editando === s ? "Cancelar" : "Editar"}</button>}
                  {(e.status === "erro" || e.status === "revisar" || e.status === "ok") && !rodando && <button onClick={() => gerar(s)} style={S.btn("#6B7280")}>Refazer</button>}
                </div>
                {e.status === "erro" && <div style={{ fontSize: 12.5, color: "#B91C1C", marginTop: 6 }}>{e.erro}</div>}
                {e.status === "revisar" && <div style={{ fontSize: 12.5, color: "#B45309", marginTop: 6 }}>Números que não constam nos dados: <b>{(e.invalidos || []).map(n => String(n).replace(".", ",")).join("; ")}</b>. Edite o texto ou refaça a seção.</div>}
                {e.dados && editando !== s && <Previa a={e.dados} />}
                {editando === s && <Editor secao={s} a={e.dados} onSalvar={n => salvarEdicao(s, n)} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Previa({ a }) {
  const p = { fontSize: 13, color: "#374151", lineHeight: 1.5, margin: "6px 0 0" };
  return (
    <div style={{ marginTop: 6, paddingLeft: 12, borderLeft: "2px solid #C9A84C" }}>
      {a.parecer && <p style={p}>{a.parecer}</p>}
      {a.conclusao && <p style={p}>{a.conclusao}</p>}
      {(a.o_que_mostram || []).map((t, i) => <p key={i} style={p}>{t}</p>)}
      {a.por_que_importa && <p style={{ ...p, color: "#6B7280" }}>{a.por_que_importa}</p>}
      {["pontos_fortes", "pontos_atencao", "prioridades"].map(k => a[k]?.length ? <p key={k} style={p}><b>{{ pontos_fortes: "Pontos fortes", pontos_atencao: "Pontos de atenção", prioridades: "Prioridades" }[k]}:</b> {a[k].join(" · ")}</p> : null)}
      {(a.recomendacoes || []).map((r, i) => <p key={i} style={{ ...p, fontSize: 12.5 }}><b>Recomendação ({r.prioridade}):</b> {r.acao} — {r.responsavel}, {r.prazo_dias} dias. {r.impacto}</p>)}
    </div>
  );
}

// Edição: uma frase por linha; recomendações no formato "ação | responsável | prazo em dias | prioridade | impacto"
function Editor({ secao, a, onSalvar }) {
  const [f, setF] = useState(() => ({
    parecer: a.parecer || "", conclusao: a.conclusao || "", por_que_importa: a.por_que_importa || "",
    o_que_mostram: (a.o_que_mostram || []).join("\n"), pontos_fortes: (a.pontos_fortes || []).join("\n"), pontos_atencao: (a.pontos_atencao || []).join("\n"), prioridades: (a.prioridades || []).join("\n"),
    recomendacoes: (a.recomendacoes || []).map(r => [r.acao, r.responsavel, r.prazo_dias, r.prioridade, r.impacto].join(" | ")).join("\n"),
  }));
  const campo = (k, rot) => <label key={k} style={{ display: "block", marginTop: 10 }}><div style={{ fontSize: 11, fontWeight: 600, color: "#0F1E3D", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3 }}>{rot}</div><textarea lang="pt-BR" spellCheck style={S.ta} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} /></label>;
  const linhas = t => t.split("\n").map(x => x.trim()).filter(Boolean);
  function salvar() {
    const n = {};
    if (secao === "resumo") Object.assign(n, { parecer: f.parecer, pontos_fortes: linhas(f.pontos_fortes), pontos_atencao: linhas(f.pontos_atencao), prioridades: linhas(f.prioridades) });
    else if (secao === "parecer") n.conclusao = f.conclusao;
    else Object.assign(n, { o_que_mostram: linhas(f.o_que_mostram), por_que_importa: f.por_que_importa, recomendacoes: linhas(f.recomendacoes).map(l => { const [acao, responsavel, prazo, prioridade, impacto] = l.split("|").map(x => (x || "").trim()); return { acao, responsavel, prazo_dias: parseInt(prazo) || null, prioridade: prioridade || "média", impacto }; }) });
    onSalvar(n);
  }
  return (
    <div style={{ marginTop: 8 }}>
      {secao === "resumo" && [campo("parecer", "Parecer (3 a 5 frases)"), campo("pontos_fortes", "Pontos fortes — um por linha"), campo("pontos_atencao", "Pontos de atenção — um por linha"), campo("prioridades", "Prioridades — uma por linha")]}
      {secao === "parecer" && campo("conclusao", "Conclusão do parecer")}
      {secao !== "resumo" && secao !== "parecer" && [campo("o_que_mostram", "O que os números mostram — uma frase por linha"), campo("por_que_importa", "Por que importa"), campo("recomendacoes", "Recomendações — ação | responsável | prazo (dias) | prioridade | impacto")]}
      <div style={{ marginTop: 10 }}><button onClick={salvar} style={S.btn("#0F1E3D", true)}>Salvar e revalidar</button></div>
    </div>
  );
}
